/**
 * Main chat widget component with message display and input
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  closeChat,
  selectChatbotOpen,
  selectChatSession,
  selectChatMessages,
  selectChatLoading,
  selectChatError,
  setSession,
  clearSession,
  addMessage,
  setMessages,
  setLoading,
  setError,
} from '../../features/chatbot/chatbotSlice';
import { useIsChatbotAllowed } from '../../hooks/useIsChatbotAllowed';
import {
  useCreateSessionMutation,
  useSendMessageMutation,
} from '../../features/chatbot/chatbotApi';

export const ChatWidget: React.FC = () => {
  const { t, i18n } = useTranslation('chatbot');
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectChatbotOpen);
  const session = useAppSelector(selectChatSession);
  const messages = useAppSelector(selectChatMessages);
  const isLoading = useAppSelector(selectChatLoading);
  const error = useAppSelector(selectChatError);
  const isAllowed = useIsChatbotAllowed();

  const [inputValue, setInputValue] = useState('');
  const [createSession] = useCreateSessionMutation();
  const [sendMessage] = useSendMessageMutation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Create session on mount if none exists
  useEffect(() => {
    if (isOpen && !session) {
      createSession({ language: i18n.language })
        .unwrap()
        .then(newSession => {
          dispatch(setSession(newSession));
        })
        .catch(err => {
          dispatch(setError(t('chatbot.sessionError')));
          console.error('Failed to create session:', err);
        });
    }
  }, [isOpen, session, createSession, dispatch, i18n.language, t]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || !session || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    dispatch(setLoading(true));
    dispatch(setError(null));

    // Add user message immediately
    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      role: 'user' as const,
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    dispatch(addMessage(tempUserMessage));

    try {
      const response = await sendMessage({
        session_id: session.id,
        message: userMessage,
        language: i18n.language,
      }).unwrap();

      // Replace temp message with actual message and add assistant response
      dispatch(
        setMessages([
          ...messages,
          response.user_message,
          response.assistant_message,
        ])
      );
      dispatch(setSession(response.session));
    } catch (err: unknown) {
      const apiError = err as { status?: number; data?: { error?: string } };

      // A 400 here usually means the persisted session_id no longer exists
      // server-side (e.g. cleared by a deploy or DB rotation). Recover by
      // starting a fresh session and retrying the send once before surfacing
      // an error to the user.
      if (apiError?.status === 400) {
        try {
          dispatch(clearSession());
          const newSession = await createSession({
            language: i18n.language,
          }).unwrap();
          dispatch(setSession(newSession));
          const response = await sendMessage({
            session_id: newSession.id,
            message: userMessage,
            language: i18n.language,
          }).unwrap();
          dispatch(
            setMessages([
              ...messages,
              response.user_message,
              response.assistant_message,
            ])
          );
          dispatch(setSession(response.session));
          return;
        } catch (retryErr) {
          console.error('Stale-session recovery failed:', retryErr);
        }
      }

      dispatch(setError(apiError?.data?.error || t('chatbot.sendError')));
      console.error('Failed to send message:', err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen || !isAllowed) return null;

  return (
    <Paper
      data-testid="chat-widget"
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: { xs: 'calc(100vw - 32px)', sm: 400 },
        height: { xs: 'calc(100vh - 100px)', sm: 600 },
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">{t('chatbot.title')}</Typography>
        <IconButton
          size="small"
          onClick={() => dispatch(closeChat())}
          sx={{ color: 'white' }}
          aria-label="close chat"
          data-testid="close-chat-button"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: 'background.default',
        }}
      >
        {messages.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
            {t('chatbot.welcome')}
          </Typography>
        )}
        {messages.map(msg => (
          <Box
            key={msg.id}
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Paper
              data-testid="chat-message"
              sx={{
                p: 1.5,
                maxWidth: '80%',
                bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                color: msg.role === 'user' ? 'white' : 'text.primary',
                '& p': { margin: '0.5em 0' },
                '& p:first-of-type': { marginTop: 0 },
                '& p:last-of-type': { marginBottom: 0 },
                '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
                '& li': { marginBottom: '0.25em' },
                '& strong': { fontWeight: 600 },
                '& code': {
                  backgroundColor:
                    msg.role === 'user'
                      ? 'rgba(255,255,255,0.2)'
                      : 'rgba(0,0,0,0.05)',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                },
              }}
            >
              <Box
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  '& > *:first-of-type': { marginTop: 0 },
                  '& > *:last-child': { marginBottom: 0 },
                }}
              >
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              </Box>
            </Paper>
          </Box>
        ))}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" onClose={() => dispatch(setError(null))}>
          {error}
        </Alert>
      )}

      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            size="small"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.inputPlaceholder')}
            disabled={isLoading || !session}
            multiline
            maxRows={3}
            aria-label={t('chatbot.inputPlaceholder')}
            data-testid="chat-input"
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading || !session}
            aria-label={t('chatbot.send')}
            data-testid="send-button"
            sx={{ minWidth: 'auto', px: 1.5, py: 1 }}
          >
            <SendIcon fontSize="small" />
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
